import React, { useMemo, useState } from 'react';
import {
  useListHolidaysQuery,
  useListSummerSchedulesQuery,
  useLoadCompanyDirectoryQuery,
} from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Python weekday (Mon=0…Sun=6) → label, for summarizing summer rules.
const PY_WEEKDAY_LABEL = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays',
  'Fridays', 'Saturdays', 'Sundays'];

// Parse "YYYY-MM-DD" as a LOCAL date (avoid the UTC shift new Date(str) causes).
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};
const toISO = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// "15:00" → "3:00 PM"
const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const fmtDate = (date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

// A holiday may be a single day or an inclusive range. Render its label.
const fmtHolidayRange = (h) => {
  const start = parseISO(h.holiday_date);
  const end = h.end_date ? parseISO(h.end_date) : null;
  if (!end || sameDay(start, end)) return fmtDate(start);
  return `${fmtDate(start)} – ${fmtDate(end)}`;
};

const segBtn = (active) => ({
  background: active ? brand.deepBlue : 'transparent',
  color: active ? 'white' : brand.charcoal,
  border: 'none',
  borderRadius: '6px',
  padding: '6px 16px',
  fontWeight: 700,
  fontSize: '0.78rem',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  cursor: 'pointer',
});

const navBtn = {
  background: 'white', color: brand.deepBlue, border: `1px solid ${brand.softGray}`,
  borderRadius: '6px', width: '34px', height: '34px', cursor: 'pointer',
  fontSize: '1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

export default function Holidays() {
  // First-of-month anchor for the displayed month; defaults to current month.
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [view, setView] = useState('calendar'); // 'calendar' | 'list'

  // On phones the 7-column grid is too narrow for chip text without skewing
  // the layout, so we hide the labels and let the cell background color carry
  // the meaning (legend + list view give the details). 576px targets phones
  // only — tablets and up keep the full labels.
  const isPhone = useIsMobile(576);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const today = new Date();

  const { data: holidays = [], isLoading: hLoading } = useListHolidaysQuery({ year });
  const { data: summer = [], isLoading: sLoading } = useListSummerSchedulesQuery({ year });
  const isLoading = hLoading || sLoading;

  // The current user's own birthday from the company directory. The public
  // directory exposes only month + day (no year), keyed by final_email, so we
  // match on the logged-in email and recur the birthday into the viewed year.
  const myEmail = (window.localStorage.getItem('e') || '').toLowerCase();
  const { data: directory = [] } = useLoadCompanyDirectoryQuery();
  const myEntry = directory.find(
    (d) => (d.final_email || '').toLowerCase() === myEmail
  );
  const birthdayMonth = myEntry?.dob_month || null;
  const birthdayDay = myEntry?.dob_day || null;

  // Build a lookup of ISO-date → array of events for the whole displayed year.
  // Holidays expand across their date range; summer rules expand into each
  // matching weekday within their range.
  const eventsByDate = useMemo(() => {
    const map = {};
    const push = (iso, ev) => { (map[iso] ||= []).push(ev); };

    holidays.forEach((h) => {
      const start = parseISO(h.holiday_date);
      const end = h.end_date ? parseISO(h.end_date) : start;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        push(toISO(d), { type: 'holiday', title: h.name });
      }
    });

    summer.forEach((s) => {
      const start = parseISO(s.start_date);
      const end = parseISO(s.end_date);
      // Python weekday (Mon=0…Sun=6) → JS getDay (Sun=0…Sat=6).
      const jsDay = (s.weekday + 1) % 7;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getDay() === jsDay) {
          push(toISO(d), {
            type: 'summer',
            title: `Early close ${fmtTime(s.end_time)}`,
          });
        }
      }
    });

    // The user's birthday recurs each year; place it on the viewed year.
    if (birthdayMonth && birthdayDay) {
      push(toISO(new Date(year, birthdayMonth - 1, birthdayDay)), {
        type: 'birthday',
        title: '🎂 Your Birthday',
      });
    }
    return map;
  }, [holidays, summer, year, birthdayMonth, birthdayDay]);

  // Build the calendar grid cells for the displayed month (leading/trailing
  // blanks padded to whole weeks).
  const weeks = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [year, month]);

  const goMonth = (delta) => setViewDate(new Date(year, month + delta, 1));
  const goToday = () => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

  // Cell background by dominant event type. The user's birthday wins over a
  // holiday, which in turn wins over a summer early-close day when several
  // fall on the same date.
  const cellBackground = (evs) => {
    if (evs.some((e) => e.type === 'birthday')) return 'rgba(125,60,152,0.3)';  // purple
    if (evs.some((e) => e.type === 'holiday')) return 'rgba(208,155,44,0.3)';   // goldenrod
    if (evs.some((e) => e.type === 'summer')) return 'rgba(0,105,166,0.15)';    // tealBlue
    return 'white';
  };

  const CHIP_COLORS = {
    holiday:  { bg: 'rgba(208,155,44,0.18)', fg: '#8a6310' },
    summer:   { bg: 'rgba(0,105,166,0.14)',  fg: brand.tealBlue },
    birthday: { bg: 'rgba(125,60,152,0.16)', fg: '#6c3483' },
  };

  const chipStyle = (type) => ({
    display: 'block',
    fontSize: '0.66rem',
    lineHeight: 1.25,
    padding: '2px 5px',
    borderRadius: '4px',
    marginTop: '3px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    background: (CHIP_COLORS[type] || CHIP_COLORS.summer).bg,
    color: (CHIP_COLORS[type] || CHIP_COLORS.summer).fg,
  });

  const cellMinHeight = isPhone ? '48px' : '92px';

  const renderCalendar = () => (
    <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden' }}>
      {/* Weekday header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: brand.deepBlue }}>
        {WEEKDAYS.map((w) => (
          <div key={w} style={{ color: 'white', textAlign: 'center', padding: '8px 0', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            {w}
          </div>
        ))}
      </div>
      {/* Day grid */}
      {weeks.map((row, ri) => (
        <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {row.map((cell, ci) => {
            if (!cell) {
              return <div key={ci} style={{ minHeight: cellMinHeight, background: '#fafafa', borderTop: `1px solid ${brand.softGray}`, borderRight: `1px solid ${brand.softGray}` }} />;
            }
            const iso = toISO(cell);
            const evs = eventsByDate[iso] || [];
            const isToday = sameDay(cell, today);
            return (
              <div key={ci} style={{
                minHeight: cellMinHeight, padding: '4px 5px',
                borderTop: `1px solid ${brand.softGray}`, borderRight: `1px solid ${brand.softGray}`,
                background: cellBackground(evs),
              }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '22px', height: '22px', borderRadius: '50%',
                  fontSize: '0.78rem', fontWeight: isToday ? 700 : 500,
                  background: isToday ? brand.goldenrod : 'transparent',
                  color: isToday ? brand.deepBlue : brand.charcoal,
                }}>
                  {cell.getDate()}
                </div>
                {!isPhone && evs.map((ev, i) => (
                  <span key={i} style={chipStyle(ev.type)} title={ev.title}>{ev.title}</span>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  const renderList = () => {
    const sortedHolidays = [...holidays].sort(
      (a, b) => parseISO(a.holiday_date) - parseISO(b.holiday_date)
    );
    const sortedSummer = [...summer].sort(
      (a, b) => parseISO(a.start_date) - parseISO(b.start_date)
    );
    const card = (title, icon, children) => (
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
        </div>
        <div style={{ padding: '0.5rem 1.25rem' }}>{children}</div>
      </div>
    );
    const row = (key, left, right) => (
      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.7rem 0', borderBottom: `1px solid ${brand.softGray}` }}>
        <span style={{ color: brand.deepBlue, fontWeight: 600, fontSize: '0.9rem' }}>{left}</span>
        <span style={{ color: brand.charcoal, fontSize: '0.85rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{right}</span>
      </div>
    );
    return (
      <>
        {birthdayMonth && birthdayDay && card('Your Birthday', 'balloon',
          row('birthday', '🎂 Your Birthday', fmtDate(new Date(year, birthdayMonth - 1, birthdayDay)))
        )}
        {card('Company Holidays', 'calendar-event',
          sortedHolidays.length === 0
            ? <p style={{ color: brand.charcoal, margin: '0.5rem 0' }}>No holidays recorded for {year}.</p>
            : sortedHolidays.map((h) => row(h.id, h.name, fmtHolidayRange(h)))
        )}
        {card('Summer Hours', 'sun',
          sortedSummer.length === 0
            ? <p style={{ color: brand.charcoal, margin: '0.5rem 0' }}>No summer hours recorded for {year}.</p>
            : sortedSummer.map((s) => row(
                s.id,
                `${s.label ? s.label + ' — ' : ''}${PY_WEEKDAY_LABEL[s.weekday]}, office closes ${fmtTime(s.end_time)}`,
                `${fmtDate(parseISO(s.start_date))} – ${fmtDate(parseISO(s.end_date))}`
              ))
        )}
      </>
    );
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>{year} Holidays</h4>
        </div>
        {/* View toggle */}
        <div style={{ display: 'inline-flex', background: 'white', borderRadius: '8px', padding: '4px', boxShadow: '0 2px 8px rgba(10,41,71,0.08)' }}>
          <button style={segBtn(view === 'calendar')} onClick={() => setView('calendar')}>
            <i className="bi bi-calendar3 me-1" />Calendar
          </button>
          <button style={segBtn(view === 'list')} onClick={() => setView('list')}>
            <i className="bi bi-list-ul me-1" />List
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: brand.charcoal }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(208,155,44,0.55)' }} /> Company holiday
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: brand.charcoal }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(0,105,166,0.45)' }} /> Winter early close
        </span>
        {birthdayMonth && birthdayDay && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: brand.charcoal }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(125,60,152,0.55)' }} /> Your birthday
          </span>
        )}
      </div>

      {/* Month navigation (calendar view only) */}
      {view === 'calendar' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <button style={navBtn} onClick={() => goMonth(-1)} aria-label="Previous month"><i className="bi bi-chevron-left" /></button>
          <button style={navBtn} onClick={() => goMonth(1)} aria-label="Next month"><i className="bi bi-chevron-right" /></button>
          <h5 style={{ color: brand.deepBlue, fontWeight: 700, margin: 0, minWidth: '180px' }}>
            {MONTHS[month]} {year}
          </h5>
          <button
            onClick={goToday}
            style={{ background: 'transparent', color: brand.tealBlue, border: `1px solid ${brand.tealBlue}`, borderRadius: '6px', padding: '5px 12px', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
          >
            Today
          </button>
        </div>
      )}

      {isLoading
        ? <p style={{ color: brand.charcoal }}>Loading…</p>
        : (view === 'calendar' ? renderCalendar() : renderList())}
    </div>
  );
}
